import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FiCalendar } from "react-icons/fi";
import postData from "../custom_functions/postData";
import { url } from "../utils/config";

// Format Y-axis to show abbreviated format (e.g., 2800 -> $2.8k)
const formatYAxis = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  return `$${value}`;
};

// Format tooltip values
const formatTooltipValue = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(2)}k`;
  }
  return `$${value}`;
};

function MaintenanceCostsChart() {
  const [selectedRange, setSelectedRange] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch data when selectedRange changes (for non-custom ranges)
  useEffect(() => {
    if (selectedRange !== "custom") {
      fetchChartData({ dateFilter: selectedRange });
    }
  }, [selectedRange]);

  const fetchChartData = (payload) => {
    setLoading(true);
    postData(
      `${url}/service_req_stats`,
      payload,
      (error) => {
        console.error("Failed to load chart data:", error);
        setChartData([]);
        setLoading(false);
      },
      (response) => {
        setChartData(response?.data || []);
        setLoading(false);
      }
    );
  };

  const handleCustomDateSubmit = () => {
    if (startDate && endDate) {
      fetchChartData({
        dateFilter: "custom",
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  const handleRangeChange = (range) => {
    setSelectedRange(range);
    if (range !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleReset = () => {
    setSelectedRange("month");
    setStartDate("");
    setEndDate("");
    fetchChartData({ dateFilter: "month" });
  };

  return (
    <div className="w-full bg-white rounded-lg flex flex-col">
      <div className="mb-4 sm:mb-6">
        {/* Header Section */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <div className="text-base sm:text-lg font-semibold mb-1">Service Costs</div>
            <div className="text-xs sm:text-sm text-gray-500">Track service expenses over time</div>
          </div>

          {/* Quick Range Selector */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            <button
              onClick={() => handleRangeChange("week")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                selectedRange === "week"
                  ? "bg-primary text-white shadow-md"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Week
            </button>
            <button
              onClick={() => handleRangeChange("month")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                selectedRange === "month"
                  ? "bg-primary text-white shadow-md"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Month
            </button>
            <button
              onClick={() => handleRangeChange("quarter")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                selectedRange === "quarter"
                  ? "bg-primary text-white shadow-md"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Quarter
            </button>
            <button
              onClick={() => handleRangeChange("year")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                selectedRange === "year"
                  ? "bg-primary text-white shadow-md"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Year
            </button>
            <button
              onClick={() => handleRangeChange("custom")}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                selectedRange === "custom"
                  ? "bg-primary text-white shadow-md"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Custom
            </button>
          </div>

          {/* Custom Date Range Section */}
          {selectedRange === "custom" && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 sm:items-end pt-3 sm:pt-4 border-t border-neutral-200">
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                <label className="text-xs sm:text-sm font-semibold text-neutral-700 flex items-center gap-2">
                  <FiCalendar size={14} className="sm:w-4 sm:h-4" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-neutral-300 bg-white text-xs sm:text-sm text-neutral-700 focus:border-primary focus:ring-2 focus:ring-primary-light transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                <label className="text-xs sm:text-sm font-semibold text-neutral-700 flex items-center gap-2">
                  <FiCalendar size={14} className="sm:w-4 sm:h-4" />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-neutral-300 bg-white text-xs sm:text-sm text-neutral-700 focus:border-primary focus:ring-2 focus:ring-primary-light transition-all"
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleCustomDateSubmit}
                  disabled={!startDate || !endDate}
                  className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                    startDate && endDate
                      ? "bg-primary text-white hover:bg-primary-dark shadow-md"
                      : "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                  }`}
                >
                  Submit
                </button>
                <button
                  onClick={handleReset}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg border border-neutral-300 bg-white text-xs sm:text-sm font-semibold text-neutral-700 hover:bg-neutral-50 transition-all"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="w-full h-[280px] sm:h-[350px] flex flex-col items-center justify-center">
        {loading ? (
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-neutral-500 text-center mt-2 text-xs sm:text-sm">Loading...</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center">
            <p className="text-neutral-500 text-center text-xs sm:text-sm">No data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="6 6" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={formatYAxis}
                domain={[0, "dataMax"]}
                tickCount={8}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value) => formatTooltipValue(value)}
                labelStyle={{ color: "#000" }}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                }}
              />
              <Line
                type="monotone"
                dataKey="service"
                name="Service"
                stroke="#27AE60"
                strokeWidth={3}
                dot={{ r: 5, stroke: "#27AE60", strokeWidth: 2, fill: "#fff" }}
                activeDot={{ r: 7 }}
              />
              <Legend verticalAlign="bottom" height={36} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default MaintenanceCostsChart;
